import UserInfoService from '~/services/UserInfoService';
import VFairService from '~/services/VFairService';
import { CandidateStudyProgram } from '~/services/types/csp';
import { VFair, VFairVisibility } from '~/services/types/vfair';

import store from '~/store';

export enum VFairAccess {
    Access = 'access',
    NoAccess = 'no-access',
    NotVerified = 'not-verified',
    NoEducation = 'no-education',
}

interface AccessResult {
    access: VFairAccess;
    vfair: VFair;
    cspsInVFair?: CandidateStudyProgram[];
}

export const calculateVFairAccess = (
    vfair: VFair,
    options: {
        awaitCSPs?: boolean;
    }
): Promise<AccessResult> => {
    const result: AccessResult = {
        access: VFairAccess.Access,
        vfair,
        cspsInVFair: undefined,
    };

    const isOpenEvent = vfair.visibility === VFairVisibility.Open;
    if (isOpenEvent) {
        return Promise.resolve({
            ...result,
            access: VFairAccess.Access,
        });
    }

    return calculateVFairAccessibility(vfair.id)
        .then(async ({ isActive, vfairSchools }) => {
            // Calculate the education entries that are participating in the vfair
            const studyPrograms: CandidateStudyProgram[] = !options.awaitCSPs
                ? store.getters['profileModule/getStudyPrograms']()
                : await store.getters['profileModule/awaitStudyPrograms']();
            const cspsInVFair = studyPrograms.filter(
                sp =>
                    !!sp.studyProgram?.schoolId && vfairSchools.includes(sp.studyProgram?.schoolId)
            );
            result.cspsInVFair = cspsInVFair;

            // If the vfair is private, the user's verified schools needs to be active on the event.
            // Otherwise, the user can enter the event if the school is a network event and the user has an education entry
            // of a school that is participating in the event.
            const canEnterEvent =
                isActive ||
                (vfair.visibility === VFairVisibility.Network && cspsInVFair.length > 0);
            if (canEnterEvent) {
                return VFairAccess.Access;
            } else {
                if (cspsInVFair.length > 0) {
                    // This means that the student has not verified the school to participate.
                    return VFairAccess.NotVerified;
                }

                // The student's school has not joined the school.
                const isCompanyAdmin = UserInfoService.getOrgAdminIds().length > 0;

                if (!isCompanyAdmin && studyPrograms.length === 0) {
                    // The student has no education in their profile
                    return VFairAccess.NoEducation;
                }

                return VFairAccess.NoAccess;
            }
        })
        .then(access => {
            result.access = access;
            return result;
        });
};

const calculateVFairAccessibility = (
    vFairId: string
): Promise<{ isActive: boolean; vfairSchools: number[]; verifiedSchools: number[] }> => {
    const output = {
        isActive: false,
        verifiedSchools: [] as number[],
        vfairSchools: [] as number[],
    };

    return VFairService.isSchoolActiveOnVfair(vFairId).then(async res => {
        output.verifiedSchools = res.verifiedSchools;
        output.vfairSchools = res.activeSchools;
        const isSchoolActive = res.isActive;
        if (isSchoolActive) {
            output.isActive = true;
        } else {
            const result = UserInfoService.getOrgAdminIds();
            if (result.length > 0) {
                output.isActive = !!(await VFairService.isAnyOrganizationActiveOnVfair(
                    vFairId,
                    result
                ));
            }
        }

        return output;
    });
};

export class VFairUtils {
    static vfairModeEventName: string = 'vfairmode:change';

    static getVFairModeEventPayload(isVFairMode: boolean) {
        return {
            isVFairMode,
        };
    }
}
